import React from "react";
import { Box, Flex, type LayoutProps, type PositionProps, Text } from "@chakra-ui/react";
import { type Customer } from "lib/sanity";
import Image from "next/image";
import { ChakraBox, ChakraSvg, ChakraText } from "ui/chakra-framer";
import { getBrandColorHex } from "theme/colors";

export type AnimatedStatCardProps = {
  customer: Customer;
  percentValue: number;
  description: string;
} & LayoutProps &
  PositionProps;

export function AnimatedStatCard({ percentValue, description, customer, ...boxProps }: AnimatedStatCardProps) {
  return (
    <ChakraBox
      pos="absolute"
      className="hero-stat-card"
      bg="brand.background.light"
      borderRadius="small"
      boxShadow="md"
      p={{ base: "18px", md: "20px", lg: "18px", xl: "20px" }}
      minW={{ base: "210px", md: "240px", lg: "210px", xl: "240px" }}
      initial={{ opacity: 0, scale: 0.9 }}
      {...boxProps}
    >
      <Box w="100%" borderBottom="1px" borderColor="brand.border.light" pb="smallest">
        <Flex align="center" gap={{ base: "tiny", md: "smallest", lg: "tiny", xl: "smallest" }} pb="tiny">
          <ArrowUpIcon />

          <ChakraText
            textStyle={{ base: "display3", md: "display2", lg: "display3", xl: "display2" }}
            lineHeight={{ base: "40px", md: "50px", lg: "40px", xl: "50px" }}
            color="new.green.900"
          >
            {percentValue}%
          </ChakraText>
        </Flex>

        <Text
          textStyle="caption1"
          color="brand.textSecondary.light"
          maxW={{ base: "170px", md: "220px", lg: "170px", xl: "220px" }}
        >
          {description}
        </Text>
      </Box>

      <Box pos="relative" w="100%" h="smaller" mt="smaller">
        <Image
          fill
          src={customer.logoUrl}
          alt={`${customer.name} logo.`}
          style={{ objectFit: "contain", objectPosition: "center left" }}
        />
      </Box>
    </ChakraBox>
  );
}

function ArrowUpIcon() {
  const green700 = getBrandColorHex("green", 700);

  return (
    <Flex
      pos="relative"
      bg="new.green.900"
      borderRadius="100%"
      boxSize={{ base: "40px", xl: "50px" }}
      align="center"
      justify="center"
      overflow="hidden"
      flexShrink={0}
    >
      <ChakraSvg
        pos="absolute"
        className="hero-stat-arrow-up"
        boxSize={{ base: "22px", xl: "28px" }}
        viewBox="0 0 28 28"
        fill="none"
        initial={{ color: green700, y: "50px" }}
      >
        <path
          d="M13.714 2.285v22.857M2.285 13.714 13.714 2.285l11.428 11.429"
          stroke="currentColor"
          strokeWidth="4.286"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </ChakraSvg>
    </Flex>
  );
}
